import { ANALYTICS_ATTRS, type AnalyticsDataAttributes } from './attrs';

export interface AnalyticsSurfaceParams {
  /**
   * Search session id, inherited by all descendants.
   *
   * The one context value that survived the move to a facts-only payload: it is a
   * runtime value with no DOM presence, so it cannot be read off markup. Entity
   * kind, entity id, availability and the surface name used to live here too and
   * were all removed — they were interpretations of the DOM, and interpretation
   * belongs to Data Analytics or to a manual Layer 2/3 event.
   */
  searchId?: string;
  /**
   * Escape hatch: declare that testids in this subtree are **not** safe to emit,
   * because they interpolate free text rather than a record id.
   *
   * Testids are trusted by default; interpolated free text is rejected by shape
   * (capitals, spaces and punctuation do not occur in a source literal). This
   * flag covers the residual case — text that happens to be lowercase and
   * unpunctuated, e.g. `` testid={`recent-search-${query}`} ``.
   *
   * Prefer fixing the testid. A lint rule ("testids name the component, not the
   * instance") is the right long-term home for the guarantee.
   */
  untrustedTestids?: boolean;
}

/**
 * Registers a container for autocapture during the pilot.
 *
 * Every interactive descendant — at any depth, mounted at any time — becomes
 * capturable with no per-element work.
 *
 * **This is a gate, and a temporary one.** It has no effect on what a captured
 * event contains: the payload is derived entirely from markup, so a click inside
 * a registered container and the same click once the gate widens to a route or to
 * `'all'` produce byte-identical events. That is what lets stage 2 of the ramp
 * *delete* these registrations without losing data, and it is pinned by
 * `rampParity.spec.tsx`.
 *
 * **Prefer passing nothing.** `analyticsSurface()` is the normal case; the only
 * property left is `searchId`, for a runtime value markup cannot express.
 *
 * @example
 * <section data-testid="rooty-search-prompts" {...analyticsSurface()}>
 */
export function analyticsSurface(
  params: AnalyticsSurfaceParams = {}
): AnalyticsDataAttributes {
  // The attribute's presence is the whole signal; it carries no value.
  const attrs: AnalyticsDataAttributes = { [ANALYTICS_ATTRS.surface]: '' };
  if (params.searchId !== undefined) {
    attrs[ANALYTICS_ATTRS.searchId] = params.searchId;
  }
  if (params.untrustedTestids === true) {
    attrs[ANALYTICS_ATTRS.untrustedTestids] = 'true';
  }
  return attrs;
}

/**
 * Marks a subtree as owned by a manual (Layer 3) tracking call, suppressing
 * autocapture inside it. Enforces the one-element-one-layer rule so a
 * hand-written `componentInteraction` can never be shadowed by an auto event.
 */
export function analyticsManualProps(): AnalyticsDataAttributes {
  return { [ANALYTICS_ATTRS.manual]: 'true' };
}

/**
 * Marks a subtree as never capturable — for anything that could render
 * sensitive or personal content.
 */
export function analyticsExcludeProps(): AnalyticsDataAttributes {
  return { [ANALYTICS_ATTRS.exclude]: 'true' };
}
